import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CorrelationIdHandler, DateColumnHandler } from './service';
import { Button, Tooltip, Spin, Alert } from 'antd';
import apiCalls from 'src/api/apiCalls';
import debounce from 'lodash.debounce';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';

const UserActivityLogsGrid = () => {
  const [copied, setCopied] = useState('');
  const [logsData, setLogsData] = useState([]);
  const [pageSize] = useState(10); // fetch 10 items per page
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const logsDataRef = useRef([]);
  const tableContainerRef = useRef(null);

  const apiUrl = process.env.REACT_APP_GATEWAYS_API + `api/identity/security-logs`;

  const fetchTableData = useCallback(async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiCalls.paginatedApiUrl(apiUrl, pageSize, currentPage);

      if (response.ok) {
        const newItems = response?.data?.items || [];
        const total = response?.data?.totalCount || 0;

        setTotalCount(total);
        setLogsData(prev => [...prev, ...newItems]);
        logsDataRef.current = [...logsDataRef.current, ...newItems];

        if (logsDataRef.current.length >= total) {
          setHasMore(false);
        } else {
          setCurrentPage(prev => prev + 1);
        }
      } else {
        setError(response.error);
      }
    } catch (e) {
      setError(e.message || 'Something went wrong while fetching logs.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, pageSize, currentPage, hasMore, loading]);

  useEffect(() => {
    fetchTableData();
  }, []);

  const handleCopy = useCallback((id) => () => {
    setCopied(id);
    setTimeout(() => setCopied(''), 300);
  }, []);

 const handleExcelExport = useCallback(() => {
  try {
    const formattedData = logsData.map((item) => ({
      Date: moment.utc(item.creationTime).local().format('DD/MM/YYYY hh:mm:ss A'),
      Action: item.action,
      'IP Address': item.clientIpAddress,
      Application: item.applicationName,
      Identity: item.identity,
      'User Name': item.userName,
      'Correlation Id': item.correlationId,
    }));

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    worksheet['!cols'] = [
      { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 25 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 30 },
    ];
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ActivityLogs');

    // Generate and download file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `ActivityLogs.xlsx`);
  
  } catch (error) {
    console.error('Excel export error:', error);
    message.error(`Export failed: ${error.message || 'Unknown error'}`);
  }
}, [logsData]);

  const handleScroll = useCallback(
    debounce(() => {
      const container = tableContainerRef.current;
      if (container) {
        const isAtBottom =
          container.scrollHeight - container.scrollTop <= container.clientHeight + 100;

        if (isAtBottom && !loading && hasMore && logsDataRef.current.length < totalCount) {
          fetchTableData();
        }
      }
    }, 100),
    [loading, hasMore, totalCount, fetchTableData]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const gridColumns = [
    {
      field: "creationTime",
      title: "Date",
      width: 300,
      render: (dataItem) => <DateColumnHandler data={{ dataItem }} />
    },
    { field: "action", title: "Action", width: 170 },
    { field: "clientIpAddress", title: "IP Address", width: 170 },
    { field: "applicationName", title: "Application", width: 220 },
    { field: "identity", title: "Identity", width: 180 },
    { field: "userName", title: "User Name", width: 180 },
    {
      field: "correlationId",
      title: "Correlation Id",
      width: 290,
      render: (dataItem) => (
        <CorrelationIdHandler
          data={dataItem}
          handleCopy={handleCopy}
          copied={copied}
        />
      )
    },
  ];

  const closeErrorHandler = useCallback(() => {
    setError(null);
  }, []);

  return (
    <div className="mt-3 wallets-grid">
      <div className="d-flex mb-3 gap-3 justify-content-end">
        <Tooltip title="Export Excel">
          <Button className="primary-btn icon-btn" onClick={handleExcelExport}>
            <span className="icon excel"></span>
          </Button>
        </Tooltip>
      </div>

      {error && (
        <div className='mb-4'>
          <Alert
            className='mb-24'
            type="error"
            description={error}
            onClose={closeErrorHandler}
            showIcon
            closable
          />
        </div>
      )}

      <div
        className="table-responsive activity-logs-table border-color rounded"
        ref={tableContainerRef}
        style={{ maxHeight: '480px', overflowY: 'auto', overflowX: 'auto' }}
      >
        <table className="table table-hover table-striped" style={{ minWidth: '1250px' }}>
          <thead>
            <tr>
              {gridColumns.map((col) => (
                <th key={col.field} style={{ width: col.width ? `${col.width}px` : 'auto' }}>
                  {col.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logsData && logsData.length > 0 ? (
              logsData.map((logItem, index) => (
                <tr key={logItem.id || index}>
                  {gridColumns.map((col) =>
                    col.render
                      ? <td key={col.field}>{col.render(logItem)}</td>
                      : <td key={col.field}>{logItem[col.field]}</td>
                  )}
                </tr>
              ))
            ) : (
              !loading && (
                <tr>
                  <td colSpan={gridColumns?.length} style={{ textAlign: 'center' }}>
                    No data available
                  </td>
                </tr>
              )
            )}
          </tbody>
          <tfoot>
            {loading && (
              <tr>
                <td colSpan={gridColumns?.length} style={{ textAlign: 'center' }}>
                  <Spin tip="Loading more..." size="small" />
                </td>
              </tr>
            )}
            {!loading && !hasMore && logsData.length > 0 && (
              <tr>
                <td colSpan={gridColumns?.length} style={{ textAlign: 'center', color: 'lightgray' }}>
                  Hey, you have seen it all!
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default UserActivityLogsGrid;
